/**
 * @file feedback-fab.ts
 * @description Guarded Floating Action Button (FAB) Web Component (<ctt-feedback-fab>)
 * for authenticated beta testing sessions, respecting mobile safe area insets,
 * session dismissibility, and route exclusions (/t/* and /overlay/*).
 * @author [F. Andy Seidl](https://www.linkedin.com/in/faseidl/)
 * @license UNLICENSED
 */

import { resolveInitialTheme } from '../display/theme-tokens.js';
import { recordPageNavigation } from './feedback-widget.js';

/*======================================================================
 * Element IDs & Tag Constants
 *======================================================================*/

/**
 * Canonical custom element tag name for the feedback FAB.
 */
export const FEEDBACK_FAB_TAG_NAME = 'ctt-feedback-fab' as const;

/**
 * Centralized, typed element IDs for feedback FAB components.
 */
export const FEEDBACK_FAB_ELEMENT_IDS = {
  container: 'ctt-feedback-fab-container',
  triggerButton: 'ctt-feedback-fab-trigger',
  dismissButton: 'ctt-feedback-fab-dismiss',
  label: 'ctt-feedback-fab-label',
  icon: 'ctt-feedback-fab-icon',
  toast: 'ctt-feedback-fab-toast',
  toastClose: 'ctt-feedback-fab-toast-close',
} as const;

export type FeedbackFabElementId =
  (typeof FEEDBACK_FAB_ELEMENT_IDS)[keyof typeof FEEDBACK_FAB_ELEMENT_IDS];

/**
 * Session storage key persisting dismissal state for the current browsing session.
 */
export const FEEDBACK_FAB_STORAGE_KEY = 'ctt_beta_fab_dismissed' as const;

/**
 * Route prefixes where the floating action button must be strictly excluded.
 */
export const FEEDBACK_FAB_EXCLUDED_PREFIXES = ['/t/', '/overlay/'] as const;

/**
 * Accessible labels and UI copy for the feedback FAB.
 */
export const FEEDBACK_FAB_LABEL_TEXT = 'Feedback' as const;
export const FEEDBACK_FAB_TRIGGER_ARIA_LABEL = 'Open Feedback' as const;
export const FEEDBACK_FAB_DISMISS_ARIA_LABEL = 'Dismiss feedback button for session' as const;
export const FEEDBACK_FAB_DISMISS_TITLE = 'Dismiss for this session' as const;
export const FEEDBACK_FAB_DISMISS_GLYPH = '✕' as const;

/**
 * UI copy and timeout for the dismissal notification toast.
 */
export const FEEDBACK_FAB_TOAST_TIMEOUT_MS = 4000;
export const FEEDBACK_FAB_TOAST_MESSAGE =
  'Feedback button hidden. You can re-enable it in ' as const;
export const FEEDBACK_FAB_PREFERENCES_PATH = '/profile' as const;

/**
 * Window custom event dispatched when feedback button visibility preference changes.
 */
export const FEEDBACK_FAB_PREFERENCE_EVENT = 'ctt:fab-preference-change' as const;

/**
 * Inline SVG markup for the chat/message bubble icon.
 */
export const FEEDBACK_FAB_ICON_SVG =
  '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="display: block; width: 18px; height: 18px;"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path></svg>';

/**
 * Scoped styles for the floating action button Web Component.
 */
export const FEEDBACK_FAB_STYLES = `
:host {
  display: block;
}

.ctt-feedback-fab-container {
  position: fixed;
  bottom: calc(var(--ctt-fab-bottom, 1rem) + env(safe-area-inset-bottom, 0px));
  right: calc(1rem + env(safe-area-inset-right, 0px));
  z-index: 900;
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid #e2e8f0;
  border-radius: 9999px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.04);
  padding: 0.35rem 0.45rem 0.35rem 0.75rem;
  gap: 0.5rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  transition: opacity 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.ctt-feedback-fab-container:hover {
  border-color: #2563eb;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  transform: translateY(-1px);
}

.ctt-feedback-fab-trigger {
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  color: #0f172a;
  font-family: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  outline: none;
}

.ctt-feedback-fab-trigger:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
  border-radius: 4px;
}

.ctt-feedback-fab-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2563eb;
}

.ctt-feedback-fab-label {
  display: inline-block;
  line-height: 1;
}

.ctt-feedback-fab-dismiss {
  background: rgba(0, 0, 0, 0.06);
  border: none;
  border-radius: 9999px;
  width: 20px;
  height: 20px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
  cursor: pointer;
  font-size: 13px;
  line-height: 1;
  transition: background 0.15s ease, color 0.15s ease;
  outline: none;
}

.ctt-feedback-fab-dismiss:hover {
  background: rgba(0, 0, 0, 0.12);
  color: #0f172a;
}

.ctt-feedback-fab-dismiss:focus-visible {
  outline: 2px solid #2563eb;
  outline-offset: 2px;
}

/* Explicit Dark Theme and System Dark Theme */
:host([data-theme="dark"]) .ctt-feedback-fab-container,
:host-context([data-theme="dark"]) .ctt-feedback-fab-container,
:host-context([theme="dark"]) .ctt-feedback-fab-container,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-container {
  background: rgba(30, 41, 59, 0.95);
  border-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
}

:host([data-theme="dark"]) .ctt-feedback-fab-container:hover,
:host-context([data-theme="dark"]) .ctt-feedback-fab-container:hover,
:host-context([theme="dark"]) .ctt-feedback-fab-container:hover,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-container:hover {
  border-color: rgba(99, 102, 241, 0.6);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
}

:host([data-theme="dark"]) .ctt-feedback-fab-trigger,
:host-context([data-theme="dark"]) .ctt-feedback-fab-trigger,
:host-context([theme="dark"]) .ctt-feedback-fab-trigger,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-trigger {
  color: #f8fafc;
}

:host([data-theme="dark"]) .ctt-feedback-fab-trigger:focus-visible,
:host-context([data-theme="dark"]) .ctt-feedback-fab-trigger:focus-visible,
:host-context([theme="dark"]) .ctt-feedback-fab-trigger:focus-visible,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-trigger:focus-visible {
  outline-color: #6366f1;
}

:host([data-theme="dark"]) .ctt-feedback-fab-icon,
:host-context([data-theme="dark"]) .ctt-feedback-fab-icon,
:host-context([theme="dark"]) .ctt-feedback-fab-icon,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-icon {
  color: #818cf8;
}

:host([data-theme="dark"]) .ctt-feedback-fab-dismiss,
:host-context([data-theme="dark"]) .ctt-feedback-fab-dismiss,
:host-context([theme="dark"]) .ctt-feedback-fab-dismiss,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-dismiss {
  background: rgba(255, 255, 255, 0.1);
  color: #94a3b8;
}

:host([data-theme="dark"]) .ctt-feedback-fab-dismiss:hover,
:host-context([data-theme="dark"]) .ctt-feedback-fab-dismiss:hover,
:host-context([theme="dark"]) .ctt-feedback-fab-dismiss:hover,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-dismiss:hover {
  background: rgba(255, 255, 255, 0.22);
  color: #ffffff;
}

:host([data-theme="dark"]) .ctt-feedback-fab-dismiss:focus-visible,
:host-context([data-theme="dark"]) .ctt-feedback-fab-dismiss:focus-visible,
:host-context([theme="dark"]) .ctt-feedback-fab-dismiss:focus-visible,
:host-context(.ctt-theme-dark) .ctt-feedback-fab-dismiss:focus-visible {
  outline-color: #6366f1;
}

@media (prefers-color-scheme: dark) {
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-container {
    background: rgba(30, 41, 59, 0.95);
    border-color: rgba(255, 255, 255, 0.15);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.35);
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-container:hover {
    border-color: rgba(99, 102, 241, 0.6);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.45);
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-trigger {
    color: #f8fafc;
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-trigger:focus-visible {
    outline-color: #6366f1;
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-icon {
    color: #818cf8;
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-dismiss {
    background: rgba(255, 255, 255, 0.1);
    color: #94a3b8;
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-dismiss:hover {
    background: rgba(255, 255, 255, 0.22);
    color: #ffffff;
  }
  :host(:not([data-theme="light"]):not([theme="light"])) .ctt-feedback-fab-dismiss:focus-visible {
    outline-color: #6366f1;
  }
}

@media (max-width: 640px) {
  .ctt-feedback-fab-label {
    display: none;
  }
  .ctt-feedback-fab-container {
    padding: 0.45rem;
  }
}

.ctt-feedback-fab-toast {
  position: fixed;
  bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  right: calc(1rem + env(safe-area-inset-right, 0px));
  z-index: 1001;
  background: rgba(30, 41, 59, 0.98);
  color: #f8fafc;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.35), 0 8px 10px -6px rgba(0, 0, 0, 0.2);
  padding: 0.625rem 0.875rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  font-size: 0.8125rem;
  animation: ctt-toast-fade-in 0.2s cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

:host([data-theme="light"]) .ctt-feedback-fab-toast,
:host-context([data-theme="light"]) .ctt-feedback-fab-toast {
  background: rgba(255, 255, 255, 0.98);
  color: #0f172a;
  border-color: #cbd5e1;
  box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.08);
}

.ctt-feedback-fab-toast-link {
  color: #38bdf8;
  text-decoration: underline;
  font-weight: 500;
}
:host([data-theme="light"]) .ctt-feedback-fab-toast-link,
:host-context([data-theme="light"]) .ctt-feedback-fab-toast-link {
  color: #2563eb;
}

.ctt-feedback-fab-toast-close {
  border: none;
  background: transparent;
  color: inherit;
  opacity: 0.7;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 2px 4px;
  border-radius: 4px;
}
.ctt-feedback-fab-toast-close:hover {
  opacity: 1;
}

@keyframes ctt-toast-fade-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
`.trim();

/**
 * Checks whether the floating feedback button is permitted on the specified route pathname.
 *
 * @param pathname - Current URL pathname to evaluate.
 * @returns True if the route is permitted, false if excluded (e.g. timer displays and overlays).
 */
export function isFabAllowedOnRoute(pathname: string): boolean {
  if (!pathname) return true;
  return !FEEDBACK_FAB_EXCLUDED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/*======================================================================
 * Custom Element Implementation
 *======================================================================*/

/**
 * Guarded floating action button Web Component for beta testing feedback.
 */
export class CttFeedbackFab extends HTMLElement {
  private _isDismissed = false;
  private _container: HTMLElement | null = null;
  private _triggerBtn: HTMLElement | null = null;
  private _dismissBtn: HTMLElement | null = null;
  private _toastEl: HTMLElement | null = null;
  private _toastTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private _handleTriggerClick: (() => void) | null = null;
  private _handleDismissClick: (() => void) | null = null;
  private _themeListener: ((e: Event) => void) | null = null;
  private _prefListener: ((e: Event) => void) | null = null;

  /**
   * Whether the FAB has been dismissed for the current session or hidden in preferences.
   */
  public get isDismissed(): boolean {
    if (this._isDismissed) return true;
    try {
      if (typeof localStorage !== 'undefined') {
        const rawPrefs = localStorage.getItem('ctt_user_preferences');
        if (rawPrefs) {
          const prefs = JSON.parse(rawPrefs);
          if (prefs && typeof prefs === 'object' && prefs.showFeedbackFab === false) {
            return true;
          }
        }
      }
      if (typeof sessionStorage !== 'undefined') {
        return sessionStorage.getItem(FEEDBACK_FAB_STORAGE_KEY) === 'true';
      }
    } catch {
      // Gracefully fall back if storage is inaccessible
    }
    return false;
  }

  /**
   * Dismisses the floating action button for the remainder of the user's session
   * and optionally renders a transient notification toast.
   *
   * @param showToast - Whether to display a temporary reminder toast. Defaults to true.
   */
  public dismiss(showToast = true): void {
    this._isDismissed = true;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.setItem(FEEDBACK_FAB_STORAGE_KEY, 'true');
      }
    } catch {
      // Gracefully fall back if sessionStorage is inaccessible
    }
    if (this._container) {
      this._container.style.display = 'none';
    }
    if (showToast) {
      this.showDismissToast();
    }
  }

  /**
   * Restores the floating action button to visible state and clears session dismissal.
   */
  public restore(): void {
    this._isDismissed = false;
    try {
      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(FEEDBACK_FAB_STORAGE_KEY);
      }
    } catch {
      // Gracefully fall back if sessionStorage is inaccessible
    }
    this.hideDismissToast();
    if (this._container) {
      this._container.style.display = 'flex';
    }
  }

  /**
   * Displays a temporary notification toast informing the user how to restore the FAB.
   */
  public showDismissToast(): void {
    this.hideDismissToast();

    const shadow = this.shadowRoot;
    if (!shadow) return;

    const toast = document.createElement('div');
    toast.id = FEEDBACK_FAB_ELEMENT_IDS.toast;
    toast.className = 'ctt-feedback-fab-toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');

    const msg = document.createElement('span');
    msg.className = 'ctt-feedback-fab-toast-msg';

    const textBefore = document.createElement('span');
    textBefore.textContent = FEEDBACK_FAB_TOAST_MESSAGE;
    msg.appendChild(textBefore);

    const link = document.createElement('a');
    link.href = FEEDBACK_FAB_PREFERENCES_PATH;
    link.className = 'ctt-feedback-fab-toast-link';
    link.textContent = 'Preferences';
    msg.appendChild(link);

    const textAfter = document.createElement('span');
    textAfter.textContent = '.';
    msg.appendChild(textAfter);

    const closeBtn = document.createElement('button') as HTMLButtonElement;
    closeBtn.id = FEEDBACK_FAB_ELEMENT_IDS.toastClose;
    closeBtn.type = 'button';
    closeBtn.className = 'ctt-feedback-fab-toast-close';
    closeBtn.setAttribute('aria-label', 'Close notification');
    closeBtn.textContent = '✕';
    closeBtn.addEventListener('click', () => {
      this.hideDismissToast();
    });

    toast.appendChild(msg);
    toast.appendChild(closeBtn);
    shadow.appendChild(toast);

    this._toastEl = toast;

    this._toastTimeoutId = setTimeout(() => {
      this.hideDismissToast();
    }, FEEDBACK_FAB_TOAST_TIMEOUT_MS);
  }

  /**
   * Hides and destroys any active dismissal reminder toast.
   */
  public hideDismissToast(): void {
    if (this._toastTimeoutId !== null) {
      clearTimeout(this._toastTimeoutId);
      this._toastTimeoutId = null;
    }
    if (this._toastEl) {
      this._toastEl.remove();
      this._toastEl = null;
    }
  }

  /**
   * Lifecycle callback invoked when mounted into the DOM.
   */
  public connectedCallback(): void {
    recordPageNavigation();

    // Check route exclusion if in a browser window context
    if (typeof window !== 'undefined' && window.location?.pathname) {
      if (!isFabAllowedOnRoute(window.location.pathname)) {
        this.style.display = 'none';
        return;
      }
    }

    if (this.isDismissed) {
      this._isDismissed = true;
    }

    if (!this.hasAttribute('data-theme')) {
      this.setAttribute('data-theme', resolveInitialTheme());
    }

    if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
      this._themeListener = (e: Event) => {
        const theme = (e as CustomEvent)?.detail?.theme;
        if (theme === 'dark' || theme === 'light') {
          this.setAttribute('data-theme', theme);
        }
      };
      window.addEventListener('ctt-theme-change', this._themeListener);

      this._prefListener = (e: Event) => {
        const show = (e as CustomEvent)?.detail?.showFeedbackFab;
        if (show === true) {
          this.restore();
        } else if (show === false) {
          this.dismiss(false);
        }
      };
      window.addEventListener(FEEDBACK_FAB_PREFERENCE_EVENT, this._prefListener);
    }

    this.renderDOM();
    this.bindEvents();

    if (this.isDismissed && this._container) {
      this._container.style.display = 'none';
    }
  }

  /**
   * Lifecycle callback invoked when disconnected from the DOM.
   */
  public disconnectedCallback(): void {
    this.unbindEvents();
    if (
      this._themeListener &&
      typeof window !== 'undefined' &&
      typeof window.removeEventListener === 'function'
    ) {
      window.removeEventListener('ctt-theme-change', this._themeListener);
      this._themeListener = null;
    }
    if (
      this._prefListener &&
      typeof window !== 'undefined' &&
      typeof window.removeEventListener === 'function'
    ) {
      window.removeEventListener(FEEDBACK_FAB_PREFERENCE_EVENT, this._prefListener);
      this._prefListener = null;
    }
    this.hideDismissToast();
  }

  private renderDOM(): void {
    const shadow = this.shadowRoot ?? this.attachShadow({ mode: 'open' });
    shadow.innerHTML = '';

    const styleEl = document.createElement('style');
    styleEl.textContent = FEEDBACK_FAB_STYLES;
    shadow.appendChild(styleEl);

    const container = document.createElement('div');
    container.id = FEEDBACK_FAB_ELEMENT_IDS.container;
    container.className = 'ctt-feedback-fab-container';

    const triggerBtn = document.createElement('button') as HTMLButtonElement;
    triggerBtn.id = FEEDBACK_FAB_ELEMENT_IDS.triggerButton;
    triggerBtn.type = 'button';
    triggerBtn.className = 'ctt-feedback-fab-trigger';
    triggerBtn.setAttribute('aria-label', FEEDBACK_FAB_TRIGGER_ARIA_LABEL);

    const iconSpan = document.createElement('span');
    iconSpan.id = FEEDBACK_FAB_ELEMENT_IDS.icon;
    iconSpan.className = 'ctt-feedback-fab-icon';
    iconSpan.innerHTML = FEEDBACK_FAB_ICON_SVG;
    triggerBtn.appendChild(iconSpan);

    const labelSpan = document.createElement('span');
    labelSpan.id = FEEDBACK_FAB_ELEMENT_IDS.label;
    labelSpan.className = 'ctt-feedback-fab-label';
    labelSpan.textContent = FEEDBACK_FAB_LABEL_TEXT;
    triggerBtn.appendChild(labelSpan);

    const dismissBtn = document.createElement('button') as HTMLButtonElement;
    dismissBtn.id = FEEDBACK_FAB_ELEMENT_IDS.dismissButton;
    dismissBtn.type = 'button';
    dismissBtn.className = 'ctt-feedback-fab-dismiss';
    dismissBtn.setAttribute('aria-label', FEEDBACK_FAB_DISMISS_ARIA_LABEL);
    dismissBtn.setAttribute('title', FEEDBACK_FAB_DISMISS_TITLE);
    dismissBtn.textContent = FEEDBACK_FAB_DISMISS_GLYPH;

    container.appendChild(triggerBtn);
    container.appendChild(dismissBtn);
    shadow.appendChild(container);

    this._container = container;
    this._triggerBtn = triggerBtn;
    this._dismissBtn = dismissBtn;
  }

  private bindEvents(): void {
    if (this._triggerBtn) {
      this._handleTriggerClick = () => {
        const event = new CustomEvent('ctt:open-feedback', {
          bubbles: true,
          composed: true,
        });
        this.dispatchEvent(event);
        if (typeof document !== 'undefined') {
          document.dispatchEvent(event);
        }
      };
      this._triggerBtn.addEventListener('click', this._handleTriggerClick);
    }

    if (this._dismissBtn) {
      this._handleDismissClick = () => {
        this.dismiss();
      };
      this._dismissBtn.addEventListener('click', this._handleDismissClick);
    }
  }

  private unbindEvents(): void {
    if (this._triggerBtn && this._handleTriggerClick) {
      this._triggerBtn.removeEventListener('click', this._handleTriggerClick);
      this._handleTriggerClick = null;
    }
    if (this._dismissBtn && this._handleDismissClick) {
      this._dismissBtn.removeEventListener('click', this._handleDismissClick);
      this._handleDismissClick = null;
    }
  }
}

// Register custom element if running in an environment with CustomElementRegistry
if (typeof customElements !== 'undefined' && !customElements.get(FEEDBACK_FAB_TAG_NAME)) {
  customElements.define(FEEDBACK_FAB_TAG_NAME, CttFeedbackFab);
}
